import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';
import { toZonedTime } from 'date-fns-tz/toZonedTime';
import { ptBR } from 'date-fns/locale';

const TZ = 'America/Sao_Paulo';

function parseEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isValid(d) ? d : null;
}

const HISTORY_TEXT_PREVIEW = 400;

function displayHistoryValue(s) {
  if (s == null || String(s).trim() === '') return '\u2014';
  return String(s);
}

function previewHistoryText(s, max = HISTORY_TEXT_PREVIEW) {
  const t = String(s ?? '');
  if (t.length <= max) return t;
  return `${t.slice(0, max)}\u2026`;
}

function parseFieldChanges(ev) {
  if (!ev || !ev.changed_fields) return { changes: null };
  try {
    const arr = JSON.parse(ev.changed_fields);
    if (!Array.isArray(arr) || arr.length === 0) return { changes: null };
    if (typeof arr[0] === 'string') {
      return {
        changes: arr.map((k) => ({ field: String(k), from: '', to: '' })),
      };
    }
    if (arr[0] && typeof arr[0] === 'object') {
      const changes = arr
        .map((row) => ({
          field: String((row.field != null ? row.field : row.Field) || ''),
          from: row.from != null ? String(row.from) : row.From != null ? String(row.From) : '',
          to: row.to != null ? String(row.to) : row.To != null ? String(row.To) : '',
        }))
        .filter((c) => c.field);
      return { changes: changes.length ? changes : null };
    }
  } catch (_) {
    /* ignore */
  }
  return { changes: null };
}

function groupEventsByDay(events) {
  const map = new Map();
  for (const ev of events) {
    const d = parseEventDate(ev.created_at);
    if (!d) continue;
    const z = toZonedTime(d, TZ);
    const key = format(z, 'yyyy-MM-dd');
    if (!map.has(key)) {
      map.set(key, { zoned: z, events: [] });
    }
    map.get(key).events.push({ ...ev, _zoned: z });
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, v]) => {
      v.events.sort((x, y) => y._zoned.getTime() - x._zoned.getTime());
      return v;
    });
}

const dayEntriesSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: '10px', md: '12px' },
  pl: '18px',
};

function defaultFieldLabel(fieldKey, translate, fieldI18nKeys) {
  const fk = String(fieldKey);
  const k = fieldI18nKeys[fk];
  if (k) return translate(k);
  return fk;
}

export default function EditHistoryTimeline({
  events = [],
  translate,
  fieldI18nKeys = {},
  emptyMessageKey,
  entryMessageKey,
  resolveFieldLabel,
}) {
  const groups = useMemo(() => groupEventsByDay(events || []), [events]);

  const labelForField = (fieldKey) => {
    if (resolveFieldLabel) {
      return resolveFieldLabel(fieldKey, translate);
    }
    return defaultFieldLabel(fieldKey, translate, fieldI18nKeys);
  };

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {translate(emptyMessageKey)}
      </Typography>
    );
  }

  return (
    <Box
      sx={() => ({
        position: 'relative',
        pl: '20px',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '5px',
          top: '8px',
          bottom: '8px',
          width: '2px',
          bgcolor: 'divider',
          borderRadius: '1px',
        },
      })}
    >
      {groups.map((group) => {
        const dayLabel = format(group.zoned, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
        const weekday = format(group.zoned, 'EEEE', { locale: ptBR });
        return (
          <Box key={format(group.zoned, 'yyyy-MM-dd')} sx={{ mb: { xs: '16px', md: '28px' } }}>
            <Box
              sx={(theme) => ({
                position: 'relative',
                mb: { xs: '8px', md: '14px' },
                pl: '18px',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '-17px',
                  top: '6px',
                  width: '11px',
                  height: '11px',
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  border: `2px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 0 0 1px ${theme.palette.divider}`,
                },
              })}
            >
              <Typography component="span" sx={{ fontWeight: 600, fontSize: '15px', color: 'text.primary' }}>
                {dayLabel}
              </Typography>
              <Typography component="span" sx={{ fontWeight: 500, fontSize: '12px', color: 'text.secondary', ml: 1 }}>
                {weekday}
              </Typography>
            </Box>
            <Box sx={dayEntriesSx}>
              {group.events.map((ev, idx) => {
                const timeStr = format(ev._zoned, 'HH:mm');
                const { changes } = parseFieldChanges(ev);
                const key = ev.id ? ev.id : `${ev.created_at}-${idx}`;
                const actor = ev.created_by_email ? ` · ${ev.created_by_email}` : '';
                return (
                  <Box key={key} sx={{ pb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {timeStr}
                      {actor}
                    </Typography>
                    {changes && changes.length > 0 ? (
                      <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                        {changes.map((c) => (
                          <li key={`${key}-${c.field}`}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                              {labelForField(c.field)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                              {previewHistoryText(`${displayHistoryValue(c.from)} → ${displayHistoryValue(c.to)}`)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {translate(entryMessageKey)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
